import React, { useState, useEffect } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import { useUser } from "../../utils/userContext";
import "react-toastify/dist/ReactToastify.css";
import {
  PDFViewer,
  pdf,
  Document,
  Page,
  Text,
  View,
  StyleSheet,
} from "@react-pdf/renderer";

const years = [ 2025, 2024, 2023];
const months = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// Invoice number generator: YYYYMMM-XXXYY
const generateInvoiceNumber = (consultantName = '', consultantEmail = '', selectedMonth = '', selectedYear = '') => {
  const year = selectedYear;
  const month = (selectedMonth.substring(0, 3) || 'XXX').toUpperCase();
  const namePart = (consultantName.substring(0, 3) || 'XXX').toUpperCase();
  let emailPart = 'XX';
  if (consultantEmail && typeof consultantEmail === 'string') {
    const emailUsername = consultantEmail.split('@')[0];
    emailPart = (emailUsername.substring(0, 2) || 'XX').toUpperCase();
  }
  return `${year}${month}-${namePart}${emailPart}`;
};

const stylesPDF = StyleSheet.create({
  page: {
    paddingTop: 18,
    paddingHorizontal: 24,
    fontSize: 10.5,
    color: "#2b2b3c",
    fontFamily: "Helvetica",
  },
  hero: {
    height: 120,
    backgroundColor: "#ffffff",
    border: "1px solid #e6e6ef",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    position: "relative",
    overflow: "hidden",
  },
  heroOrange: {
    position: "absolute",
    right: 0,
    top: 0,
    width: 260,
    height: 120,
    backgroundColor: "#25B740",
    borderBottomLeftRadius: 80,
  },
  heroTitle: {
    position: "absolute",
    right: 24,
    top: 34,
    fontSize: 22,
    color: "#ffffff",
    letterSpacing: 1.5,
  },
  row: { flexDirection: "row", gap: 18, marginTop: 18 },
  col: { flex: 1 },
  hOrange: { color: "#25B740", fontSize: 12, fontWeight: 700, marginBottom: 8 },
  entityName: { fontSize: 11, fontWeight: 700, marginBottom: 4 },
  kv: { flexDirection: "row", marginBottom: 3 },
  kvKey: { width: 86, color: "#7a7a8a" },
  kvVal: { flex: 1, color: "#2b2b3c", whiteSpace: "pre" },
  table: {
    marginTop: 18,
    border: "1px solid #d7d7e0",
    borderRadius: 6,
    overflow: "hidden",
  },
  thead: {
    flexDirection: "row",
    backgroundColor: "#004F7C",
    color: "#fff",
    paddingVertical: 8,
    paddingHorizontal: 10,
  },
  th: { fontSize: 10, fontWeight: 700 },
  tr: {
    flexDirection: "row",
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderTop: "1px solid #ececf3",
  },
  td: { fontSize: 10 },
  totalsCol: { flex: 1, paddingLeft: 18 },
  totalRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 6,
  },
  totalKey: { color: "#7a7a8a" },
  totalVal: { fontWeight: 700 },
  totalFooter: {
    marginTop: 10,
    backgroundColor: "#004F7C",
    color: "#fff",
    borderRadius: 6,
    paddingVertical: 10,
    paddingHorizontal: 12,
    flexDirection: "row",
    justifyContent: "space-between",
  },
  totalFooterKey: { fontSize: 12, fontWeight: 700 },
  totalFooterVal: { fontSize: 12, fontWeight: 700 },
  bullet: { flexDirection: "row", marginBottom: 4, marginRight: 8 },
  dot: { width: 10, textAlign: "center" },
  bulletText: { flex: 1, lineHeight: 1.4 },
});

const PdfInvoice = ({ data }) => {
  const api = data || {};
  const currency = api.currency || api.assignmentTotalHours?.currency || "£";
  const paymentType = (
    api.paymentType ||
    api.assignmentTotalHours?.paymentType ||
    "fixed"
  ).toLowerCase();
  const startDate = api.assignmentTotalHours?.startDate;
  const endDate = api.assignmentTotalHours?.endDate;
  const taxPct = Number(api.tax ?? api.assignmentTotalHours?.tax ?? 0);

  const partner = {
    companyName: api.agencyDetails?.PartnerName || api.partnerName || "",
    address: api.agencyDetails?.Address || "",
    email: api.agencyDetails?.Email || api.partnerName || "",
    phone: api.agencyDetails?.ContactNumber || "",
  };

  const vm = api.vendorManagement || {};
  const vendor = {
    companyName: vm.billableName || "",
    address: vm.vendorAddress || "",
    email: vm.vendorEmail || "",
    phone: vm.vendorContact || "",
    gstNumber: vm.vendorGSTNumber || "",
  };

  const bank = api.bankDetails || {
    accountHolder: api.agencyDetails?.PartnerName || "",
    bankName: api.agencyDetails?.BankName || "",
    accountNumber: api.agencyDetails?.AccountNumber || "",
    sortCode: api.agencyDetails?.SortCode || "",
    iban: api.agencyDetails?.IBAN || "",
    swift: api.agencyDetails?.SWIFT || "",
  };


  const totalHours = Number(api.assignmentTotalHours?.totalHours ?? 0);
  const unitPrice = Number(api.assignmentTotalHours?.price ?? api.price ?? 0);

  // Calculate unit for all payment types (case-insensitive exact match)
  let unit = 1;
  const paymentTypeLower = (paymentType || "").toLowerCase();
  let itemDescription = `Resource Engagement (${api.FullName || api.EmployeeId || ""})`;
  if (paymentTypeLower === "hourly") {
    unit = totalHours || 0;
  } else if (paymentTypeLower === "day rate") {
    // Calculate days as totalHours / 8 (ignore date range)
    unit = totalHours ? totalHours / 8 : 0;
    itemDescription += " (8 hours per day)";
  } else if (paymentTypeLower === "fixed") {
    unit = 1;
  } else {
    unit = 1;
  }

  const defaultItems = [
    {
      itemDescription,
      startDate,
      endDate,
      currency,
      quantity: unit,
      rate:
        paymentTypeLower === "hourly"
          ? unitPrice || 0
          : paymentTypeLower === "day rate"
          ? unitPrice || 0 // still daily rate, unit is number of days
          : Number(api.assignmentTotalHours?.subTotal ?? api.price ?? 0),
      tax: taxPct,
    },
  ];

  const items =
    Array.isArray(api.items) && api.items.length ? api.items : defaultItems;

  const lineSubtotal = (q, r) => Number(q || 0) * Number(r || 0);
  const computedSub = items.reduce(
    (s, it) => s + lineSubtotal(it.quantity, it.rate),
    0
  );
  const computedTax = items.reduce(
    (s, it) =>
      s + lineSubtotal(it.quantity, it.rate) * (Number(it.tax || 0) / 100),
    0
  );

  const subtotal = Number(
    api.totals?.subTotal ?? api.assignmentTotalHours?.subTotal ?? computedSub
  );
  const taxTotal = Number(
    api.totals?.taxTotal ?? api.assignmentTotalHours?.taxTotal ?? computedTax
  );
  const total = Number(
    api.totals?.grandTotal ??
    api.assignmentTotalHours?.grandTotal ??
    subtotal + taxTotal
  );

  const uniqueTax = [...new Set(items.map((it) => Number(it.tax || 0)))];
  const taxLabel =
    uniqueTax.length <= 1
      ? `Tax(${(uniqueTax[0] ?? 0).toFixed(2)}%)`
      : "Tax(Multiple)";

  const fmtDate = (d) => {
    if (!d) return "";
    try {
      const x = typeof d === "string" ? new Date(d) : d;
      return x.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    } catch {
      return d;
    }
  };
  const money = (n) => `${currency} ${Number(n || 0).toFixed(2)}`;

  return (
    <Document>
      <Page size="A4" style={stylesPDF.page} wrap>
        <View style={stylesPDF.hero}>
          <View style={stylesPDF.heroOrange} />
          <Text style={stylesPDF.heroTitle}>INVOICE</Text>
        </View>

        <View style={stylesPDF.row}>
          <View style={stylesPDF.col}>
            <Text style={stylesPDF.hOrange}>Invoice Details</Text>
            <View style={stylesPDF.kv}>
              <Text style={stylesPDF.kvKey}>Invoice Date:</Text>
              <Text style={stylesPDF.kvVal}>
                {fmtDate(api?.invoiceDate) || fmtDate(new Date())}
              </Text>
            </View>
            <View style={stylesPDF.kv}>
              <Text style={stylesPDF.kvKey}>Invoice No:</Text>
              <Text style={stylesPDF.kvVal}>
                {api?.invoiceNumber}
              </Text>
            </View>
          </View>
          <View style={stylesPDF.col} />
        </View>

        <View style={stylesPDF.row}>
          <View style={stylesPDF.col}>
            <Text style={stylesPDF.hOrange}>Invoice From</Text>
            <Text style={stylesPDF.entityName}>{partner.companyName}</Text>
            <View style={stylesPDF.kv}>
              <Text style={stylesPDF.kvKey}>Address:</Text>
              <Text style={stylesPDF.kvVal}>{partner.address}</Text>
            </View>
            <View style={stylesPDF.kv}>
              <Text style={stylesPDF.kvKey}>Email:</Text>
              <Text style={stylesPDF.kvVal}>{partner.email}</Text>
            </View>
            <View style={stylesPDF.kv}>
              <Text style={stylesPDF.kvKey}>Phone no:</Text>
              <Text style={stylesPDF.kvVal}>{partner.phone}</Text>
            </View>
          </View>

          <View style={stylesPDF.col}>
            <Text style={stylesPDF.hOrange}>Invoice To</Text>
            <Text style={stylesPDF.entityName}>
              {vendor.companyName || "-"}
            </Text>
            <View style={stylesPDF.kv}>
              <Text style={stylesPDF.kvKey}>Address:</Text>
              <Text style={stylesPDF.kvVal}>{vendor.address || "-"}</Text>
            </View>
            <View style={stylesPDF.kv}>
              <Text style={stylesPDF.kvKey}>Email:</Text>
              <Text style={stylesPDF.kvVal}>{vendor.email || "-"}</Text>
            </View>
            <View style={stylesPDF.kv}>
              <Text style={stylesPDF.kvKey}>Phone no:</Text>
              <Text style={stylesPDF.kvVal}>{vendor.phone || "-"}</Text>
            </View>
          </View>
        </View>

        <View style={stylesPDF.table}>
          <View style={stylesPDF.thead}>
            <Text style={[stylesPDF.th, { flex: 3 }]}>Description</Text>
            <Text style={[stylesPDF.th, { flex: 1 }]}>Start Date</Text>
            <Text style={[stylesPDF.th, { flex: 1 }]}>End Date</Text>
            <Text style={[stylesPDF.th, { flex: 1 }]}>Currency</Text>
            <Text style={[stylesPDF.th, { flex: 0.7 }]}>Unit</Text>
            <Text style={[stylesPDF.th, { flex: 1.3 }]}>Billing Rate</Text>
          </View>
          {items.map((it, i) => (
            <View key={i} style={stylesPDF.tr}>
              <Text style={[stylesPDF.td, { flex: 3 }]}>
                {it.itemDescription || "-"}
              </Text>
              <Text style={[stylesPDF.td, { flex: 1 }]}>
                {fmtDate(startDate)}
              </Text>
              <Text style={[stylesPDF.td, { flex: 1 }]}>
                {fmtDate(endDate)}
              </Text>
              <Text style={[stylesPDF.td, { flex: 1 }]}>
                {it.currency || currency}
              </Text>
              <Text style={[stylesPDF.td, { flex: 0.7 }]}> 
                {Number(it.quantity)}{paymentTypeLower === "day rate" ? " days" : ""}
              </Text>
              <Text style={[stylesPDF.td, { flex: 1.3 }]}>
                {money(it.rate)}
              </Text>
            </View>
          ))}
        </View>

        <View style={stylesPDF.row}>
          <View style={stylesPDF.col}>
            <Text style={stylesPDF.hOrange}>Bank Details:</Text>
            <View style={stylesPDF.kv}>
              <Text style={stylesPDF.kvKey}>Account Holder:</Text>
              <Text style={stylesPDF.kvVal}>{bank.accountHolder}</Text>
            </View>
            <View style={stylesPDF.kv}>
              <Text style={stylesPDF.kvKey}>Bank Name:</Text>
              <Text style={stylesPDF.kvVal}>{bank.bankName}</Text>
            </View>
            <View style={stylesPDF.kv}>
              <Text style={stylesPDF.kvKey}>Account Number:</Text>
              <Text style={stylesPDF.kvVal}>{bank.accountNumber}</Text>
            </View>
            <View style={stylesPDF.kv}>
              <Text style={stylesPDF.kvKey}>Sort Code:</Text>
              <Text style={stylesPDF.kvVal}>{bank.sortCode}</Text>
            </View>
            <View style={stylesPDF.kv}>
              <Text style={stylesPDF.kvKey}>IBAN:</Text>
              <Text style={stylesPDF.kvVal}>{bank.iban}</Text>
            </View>
            <View style={stylesPDF.kv}>
              <Text style={stylesPDF.kvKey}>SWIFT:</Text>
              <Text style={stylesPDF.kvVal}>{bank.swift}</Text>
            </View>
          </View>

          <View style={stylesPDF.totalsCol}>
            <View style={stylesPDF.totalRow}>
              <Text style={stylesPDF.totalKey}>Subtotal</Text>
              <Text style={stylesPDF.totalVal}>{money(subtotal)}</Text>
            </View>
            <View style={stylesPDF.totalRow}>
              <Text style={stylesPDF.totalKey}>{taxLabel}</Text>
              <Text style={stylesPDF.totalVal}>{money(taxTotal)}</Text>
            </View>
            <View style={stylesPDF.totalFooter}>
              <Text style={stylesPDF.totalFooterKey}>Total</Text>
              <Text style={stylesPDF.totalFooterVal}>{money(total)}</Text>
            </View>
          </View>
        </View>

        <View style={{ marginTop: 16 }}>
          <Text style={stylesPDF.hOrange}>Terms and Conditions:</Text>
          <View style={stylesPDF.bullet}>
            <Text style={stylesPDF.dot}>•</Text>
            <Text style={stylesPDF.bulletText}>
              Payment will be made by the Customer within 30 days upon receipt
              of an invoice.
            </Text>
          </View>
          <View style={stylesPDF.bullet}>
            <Text style={stylesPDF.dot}>•</Text>
            <Text style={stylesPDF.bulletText}>
              Upon receiving the Services or Deliverable, the customer has a
              seven-day period to carry out acceptance tests as outlined in the
              acceptance criteria.
            </Text>
          </View>
          <View style={stylesPDF.bullet}>
            <Text style={stylesPDF.dot}>•</Text>
            <Text style={stylesPDF.bulletText}>
              Confidential information shall mean any information disclosed by
              one party to the other party, including without limitation
              documents, business plans, and source code.
            </Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

function VendorInvoicesPage() {
  const { user } = useUser();
  const token = localStorage.getItem("token");

  const [employees, setEmployees] = useState([]);
  const [selectedConsultant, setSelectedConsultant] = useState("");
  const [selectedYear, setSelectedYear] = useState(years[1]);
  const [selectedMonth, setSelectedMonth] = useState(months[7]);
  const [invoice, setInvoice] = useState(null);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [organisationVendorDetails, setOrganisationVendorDetails] = useState(null);
  const [invoiceExists, setInvoiceExists] = useState(false);
  const [showInvoicePreview, setShowInvoicePreview] = useState(false);

  // Email modal state
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    setInvoice(null);
    setInvoiceNumber("");
    setInvoiceExists(false);
    setShowInvoicePreview(false);
  }, [selectedConsultant, selectedYear, selectedMonth]);

  const getApiUrl = () => `${process.env.REACT_APP_BE_URL}`;
  const handle401Error = (err) => {
    if (err?.response?.status === 401) {
      toast.error("Session expired. Please login again.", {
        position: toast.POSITION.TOP_CENTER,
      });
      localStorage.removeItem("userInfo");
      window.location.href = "/auth/login";
      return true;
    }
    return false;
  };
  const blobToBase64 = (blob) =>
    new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onloadend = () => resolve(r.result.split(",")[1]);
      r.onerror = reject;
      r.readAsDataURL(blob);
    });

  useEffect(() => {
    fetchEmployees();
    fetchOrganisationVendorDetails();
  }, []);

  async function fetchEmployees() {
    setLoading(true);
    try {
      const res = await axios.get(`${getApiUrl()}/getEmployeeMasterReport`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = res?.data?.onboardEmployeeDetails || [];
      const loginEmail = (user?.FirstName || "").toLowerCase();
      const filtered = list.filter(
        (e) =>
          e.Status === "Active" &&
          e.EmployeeType === "Consultant" &&
          (e.vendorName || "").toLowerCase() === loginEmail
      );
      setEmployees(filtered);
      if (filtered.length > 0) setSelectedConsultant(filtered[0].EmployeeId);
      else setSelectedConsultant("");
    } catch (err) {
      if (!handle401Error(err))
        toast.error(
          err?.response?.data?.message || "Failed to fetch employees.",
          { position: toast.POSITION.TOP_CENTER }
        );
    } finally {
      setLoading(false);
    }
  }

  async function fetchOrganisationVendorDetails() {
    try {
      const url = `${getApiUrl()}/getConfigurationCenter`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (
        response.status === 200 &&
        response.data &&
        response.data.vendorManagement
      ) {
        setOrganisationVendorDetails(response.data.vendorManagement);
      } else {
        setOrganisationVendorDetails(null);
      }
    } catch (error) {
      setOrganisationVendorDetails(null);
    }
  }

  async function checkIfInvoiceNumberExists(invoiceNo) {
    try {
      // Adjust this API and result parsing as per your backend!
      const res = await axios.get(
        `${getApiUrl()}/getConsultantInvoices?agency=${invoiceNo}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return (
        Array.isArray(res?.data?.invoices) &&
        res.data.invoices.length > 0
      );
    } catch {
      return false;
    }
  }

  const handleGenerateInvoice = async () => {
    if (!selectedConsultant) {
      toast.error("Please select a consultant.", {
        position: toast.POSITION.TOP_CENTER,
      });
      return;
    }

    setLoading(true);
    setShowInvoicePreview(false);

    const selectedConsultantObj = employees.find(
      (e) => e.EmployeeId === selectedConsultant
    );
    const consultantName = selectedConsultantObj?.EmployeeId || "";
    const consultantEmail = selectedConsultantObj?.OfficialEmail || "";
    const invoiceNo = generateInvoiceNumber(
      consultantName,
      consultantEmail,
      selectedMonth,
      selectedYear
    );
    setInvoiceNumber(invoiceNo);

    try {
      const monthIndex = String(months.indexOf(selectedMonth) + 1).padStart(2, "0");
      const invURL = `${getApiUrl()}/getInvoiceDetails?employeeId=${selectedConsultant}&year=${selectedYear}&month=${monthIndex}`;
      const headers = {
        headers: { Authorization: `Bearer ${token}` },
      };
      const invRes = await axios.get(invURL, headers);

      if (invRes.status === 200 && invRes.data) {
        // Now check if invoice number exists (only when 200)
        const exists = await checkIfInvoiceNumberExists(invoiceNo);
        if (exists) {
          setInvoice({ ...invRes.data, invoiceNumber: invoiceNo });
          setInvoiceExists(true);
          setShowInvoicePreview(false);
          toast.info(
            `Invoice ${invoiceNo} is already generated for this consultant and period. Please check the dashboard.`,
            { position: toast.POSITION.TOP_CENTER }
          );
        } else {
          setInvoice({ ...invRes.data, invoiceNumber: invoiceNo });
          setInvoiceExists(false);
          setShowInvoicePreview(true);
        }
      }
    } catch (err) {
      setInvoice(null);
      setInvoiceExists(false);
      setShowInvoicePreview(false);

      if (err?.response?.status === 404) {
        toast.error(
          "No invoice/timesheet data found for this period.",
          { position: toast.POSITION.TOP_CENTER }
        );
        // No check for invoice number in 404 case!
      } else if (!handle401Error(err)) {
        toast.error(
          err?.response?.data?.message || "Failed to fetch invoice details.",
          { position: toast.POSITION.TOP_CENTER }
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateClick = async () => {
    setShowInvoicePreview(true);
    // Optionally, fetch latest invoice details by invoiceNumber if you wish.
    // const res = await axios.get(`${getApiUrl()}/getConsultantInvoices?invoiceNumber=${invoiceNumber}`, {
    //   headers: { Authorization: `Bearer ${token}` },
    // });
    // if (res.data?.consultantInvoices?.[0]) {
    //   setInvoice(res.data.consultantInvoices[0]);
    // }
  };

  const handleDownloadPDF = async () => {
    try {
      if (!invoice) return toast.error("No invoice to download.");
      setLoading(true);
      const pdfDoc = (
        <PdfInvoice
          data={{
            ...invoice,
            invoiceNumber,
            vendorManagement: organisationVendorDetails,
          }}
        />
      );
      const blob = await pdf(pdfDoc).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safe = (invoice.fullName || "consultant")
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");
      a.download = `${safe}_invoice_${invoiceNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Invoice downloaded.", {
        position: toast.POSITION.TOP_CENTER,
      });
    } catch (e) {
      toast.error(`Failed to download PDF: ${e.message}`, {
        position: toast.POSITION.TOP_CENTER,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInvoiceUpload = async () => {
    if (!invoice && !invoiceExists) {
      toast.error("Please generate an invoice before submitting.", {
        position: toast.POSITION.TOP_CENTER,
      });
      return;
    }
    setUploading(true);
    try {
      let invoiceNo = invoiceNumber;
      let updatedInvoice = { ...invoice, invoiceNumber: invoiceNo };
      setInvoiceNumber(invoiceNo);
      setInvoice(updatedInvoice);

      const selectedConsultantObj = employees.find(
        (e) => e.EmployeeId === selectedConsultant
      );
      const consultantEmail = selectedConsultantObj?.OfficialEmail || "";
      const startDate = invoice?.assignmentTotalHours?.startDate || "";
      const endDate = invoice?.assignmentTotalHours?.endDate || "";
      const partner = invoice?.agencyDetails || invoice?.partner || {};
      const agencyName =
        partner.PartnerName ||
        partner.companyName ||
        invoice?.agencyName ||
        "";
      const agencyEmail =
        partner.Email ||
        partner.email ||
        invoice?.partnerEmail ||
        invoice?.email ||
        "";
      const pdfDoc = (
        <PdfInvoice
          data={{
            ...updatedInvoice,
            vendorManagement: organisationVendorDetails,
          }}
        />
      );
      const pdfBlob = await pdf(pdfDoc).toBlob();
      const formData = new FormData();
      formData.append("InvoiceNo", invoiceNo);
      formData.append("Agency", agencyName);
      formData.append("AgencyEmail", agencyEmail);
      formData.append("ConsultantEmail", consultantEmail);
      formData.append("StartDate", startDate);
      formData.append("EndDate", endDate);
      formData.append("Status", "pending");
      formData.append(
        "invoiceUpload",
        pdfBlob,
        `${invoiceNo || "invoice"}.pdf`
      );
      await axios.post(`${getApiUrl()}/invoiceupload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      toast.success("Invoice uploaded successfully!", {
        position: toast.POSITION.TOP_CENTER,
      });
      // Reset page state after successful upload
    setInvoice(null);
    setInvoiceNumber("");
    setInvoiceExists(false);
    setShowInvoicePreview(false);
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Failed to upload invoice.",
        { position: toast.POSITION.TOP_CENTER }
      );
    } finally {
      setUploading(false);
    }
  };

  const openEmailModal = () => {
    if (!invoice) return toast.error("No invoice to email.");
    setEmailTo(invoice.partnerEmail || "");
    setEmailModalOpen(true);
  };
  const closeEmailModal = () => setEmailModalOpen(false);

  const sendEmailNow = async () => {
    if (!emailTo) {
      toast.error("Please enter recipient email.");
      return;
    }
    try {
      setSendingEmail(true);
      const pdfDoc = (
        <PdfInvoice
          data={{
            ...invoice,
            invoiceNumber,
            vendorManagement: organisationVendorDetails,
          }}
        />
      );
      const pdfBlob = await pdf(pdfDoc).toBlob();
      const base64Pdf = await blobToBase64(pdfBlob);
      const safe = (invoice.fullName || "consultant")
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");
      await axios.post(
        `${getApiUrl()}/emailAPI`,
        {
          from: "support@bookyourtime.co.uk",
          to: [emailTo],
          subject: `Invoice ${invoiceNumber}`,
          html: `<p>Dear ${invoice.fullName || "Consultant"
            },</p><p>Please find attached invoice <b>${invoiceNumber}</b> for your review.</p><p>Regards,<br/>Sriven Solutions</p>`,
          filename: `${safe}_invoice_${invoiceNumber}.pdf`,
          content: base64Pdf,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success("Invoice emailed successfully!", {
        position: toast.POSITION.TOP_CENTER,
      });
      setEmailModalOpen(false);
    } catch (err) {
      if (!handle401Error(err))
        toast.error(
          err?.response?.data?.message || "Failed to send email.",
          { position: toast.POSITION.TOP_CENTER }
        );
    } finally {
      setSendingEmail(false);
    }
  };

  const periodLabel = invoice
    ? `${new Date(invoice.startDate).toLocaleDateString("en-GB", {
      month: "long",
      year: "numeric",
    })}`
    : "";

  return (
    <div className="content-page">
      {loading && (
        <div id="preloading">
          <span className="loader"></span>
        </div>
      )}
      <ToastContainer />

      <div className="container-fluid">
        <div className="row mb-2">
          <div className="col">
            <h3 className="title mb-2">Invoice Management</h3>
          </div>
        </div>

        <div className="row justify-content-center">
          <div className="col-lg-10">
            <div
              className="card shadow-sm border-0 mb-3"
              style={{ borderRadius: 16, background: "#fafafa" }}
            >
              <div className="card-body">
                <div className="row align-items-end">
                  <div className="col-md-5 mb-3 mb-md-0">
                    <label className="font-weight-bold">Select Consultant:</label>
                    <select
                      className="form-control"
                      value={selectedConsultant}
                      onChange={(e) => {
                        setSelectedConsultant(e.target.value);
                        setShowInvoicePreview(false);
                      }}
                    >
                      <option value="" disabled>Select a consultant</option>
                      {employees.map((emp) => (
                        <option key={emp.EmployeeId} value={emp.EmployeeId}>
                          {emp.FirstName} {emp.LastName} ({emp.Email || emp.EmployeeId})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-3 mb-3 mb-md-0">
                    <label className="font-weight-bold">Select Year:</label>
                    <select
                      className="form-control"
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(Number(e.target.value))}
                    >
                      {years.map((y) => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-3 mb-3 mb-md-0 mt-3 ms-3">
                    <label className="font-weight-bold">Select Month:</label>
                    <select
                      className="form-control"
                      value={selectedMonth}
                      onChange={(e) => setSelectedMonth(e.target.value)}
                    >
                      {months.map((m) => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                    </select>
                  </div>
                  {/* Hide "View"/"Generate" button if invoiceExists and not in update/preview mode */}
                  {!invoiceExists || showInvoicePreview ? (
                    <button
                      onClick={invoice ? undefined : handleGenerateInvoice}
                      className="btn btn-primary"
                      style={{
                        color: "#fff",
                        borderRadius: 8,
                        fontWeight: 600,
                        width: "150px",
                        minWidth: "150px",
                        marginTop: "20px",
                        marginLeft: "425px",
                      }}
                      disabled={!selectedConsultant}
                      title={
                        invoice
                          ? "Invoice already generated for this period"
                          : "Generate Invoice"
                      }
                    >
                      {invoice ? "View" : "Generate"}
                    </button>
                  ) : null}
                </div>
              </div>
            </div>

            {/* Info bar with Update button when invoice exists */}
            {invoiceExists && !showInvoicePreview && (
              <div className="alert alert-warning d-flex justify-content-between align-items-center" style={{ borderRadius: 8 }}>
                <span>
                  Invoice <b>{invoiceNumber}</b> is already generated for this consultant and period.
                  Please check the dashboard.
                </span>
                <button
                  className="btn btn-outline-success"
                  onClick={handleUpdateClick}
                  disabled={loading}
                >
                  Update
                </button>
              </div>
            )}

            {/* Show invoice preview + actions when in update mode or after normal generation */}
            {showInvoicePreview && invoice && (
              <div>
                <div
                  className="card border-0 shadow-sm mt-3"
                  style={{ borderRadius: 16, background: "#e9f4ff" }}
                >
                  <div
                    className="card-header d-flex align-items-center justify-content-between"
                    style={{
                      background: "#e8f2ff",
                      borderTopLeftRadius: 16,
                      borderTopRightRadius: 16,
                    }}
                  >
                    <div>
                      <div className="small text-muted">Existing Invoice</div>
                      <div className="mt-1" style={{ fontSize: 12 }}>
                        <b>Invoice No:</b> {invoiceNumber}
                        &nbsp;&nbsp;|&nbsp;&nbsp;
                        <b>Consultant:</b> {invoice?.EmployeeId}
                        &nbsp;&nbsp;|&nbsp;&nbsp;
                        <b>Status:</b> {String("Pending").toLowerCase()}
                        &nbsp;&nbsp;|&nbsp;&nbsp;
                        <b>Vendor:</b> {invoice.agencyDetails?.PartnerName || "N/A"}
                      </div>
                      <div style={{ fontSize: 12, color: "#5d6470" }}>
                        <b>Period:</b>{" "}
                        {invoice?.assignmentTotalHours?.startDate
                          ? new Date(
                              invoice.assignmentTotalHours.startDate
                            ).toLocaleDateString()
                          : "—"}{" "}
                        /{" "}
                        {invoice?.assignmentTotalHours?.endDate
                          ? new Date(
                              invoice.assignmentTotalHours.endDate
                            ).toLocaleDateString()
                          : "—"}
                        &nbsp;&nbsp;|&nbsp;&nbsp;<b>Email:</b>{" "}
                        {invoice.partnerName || "—"}
                      </div>
                    </div>

                    <div className="d-flex align-items-center">
                      <button
                        className="btn btn-outline-primary mr-2"
                        onClick={handleDownloadPDF}
                        style={{ borderRadius: 8 }}
                      >
                        <i className="ri-download-2-line mr-1" /> Download PDF
                      </button>
                      <button
                        className="btn btn-success mr-2"
                        onClick={handleInvoiceUpload}
                        style={{ borderRadius: 8 }}
                        disabled={uploading}
                        title="Submit generated PDF to Admin"
                      >
                        {uploading ? "Submitting..." : "Submit"}
                      </button>
                    </div>
                  </div>

                  <div
                    className="card-body"
                    style={{
                      background: "#fff",
                      borderBottomLeftRadius: 16,
                      borderBottomRightRadius: 16,
                    }}
                  >
                    <div style={{ height: 680 }}>
                      <PDFViewer style={{ width: "100%", height: "100%" }}>
                        <PdfInvoice
                          data={{
                            ...invoice,
                            invoiceNumber,
                            vendorManagement: organisationVendorDetails,
                          }}
                        />
                      </PDFViewer>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Show initial message if nothing selected */}
            {!invoice && !invoiceExists && !showInvoicePreview && (
              <div
                className="card shadow-sm border-0"
                style={{ borderRadius: 16, background: "#f7f9fd" }}
              >
                <div className="card-body text-center py-5">
                  <div
                    style={{
                      width: 56,
                      height: 56,
                      margin: "0 auto 16px",
                      borderRadius: "50%",
                      background: "#eaf1ff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 24,
                      color: "#7aa7ff",
                    }}
                  >
                    ↑
                  </div>
                  <h5 style={{ color: "#3a3a55", fontWeight: 700 }}>
                    Generate Your Invoice
                  </h5>
                  <p
                    style={{
                      color: "#5d6470",
                      maxWidth: 560,
                      margin: "8px auto 0",
                    }}
                  >
                    Select a consultant, year, and month, then click{" "}
                    <b>"Generate Invoice"</b> to create your invoice. Once
                    generated, you can download or email your invoice.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ====================== EMAIL POPUP ====================== */}
      {emailModalOpen && (
        <div style={modalStyles.backdrop}>
          <div style={modalStyles.modal}>
            <div style={modalStyles.header}>
              <span style={{ fontSize: 18, fontWeight: 700, color: "#2d3748" }}>
                ✉️ Send Invoice by Email
              </span>
              <button onClick={closeEmailModal} style={modalStyles.closeBtn}>
                ×
              </button>
            </div>
            <div style={modalStyles.body}>
              <div style={modalStyles.summary}>
                <div style={{ color: "#2d3748", lineHeight: 1.6 }}>
                  <div>
                    <strong>Invoice:</strong> {invoiceNumber} -{" "}
                    {invoice?.fullName}
                  </div>
                  <div>
                    <strong>Period:</strong> {periodLabel}
                  </div>
                </div>
              </div>
              <label style={modalStyles.label}>
                <span style={{ color: "#e53e3e" }}>*</span> Recipient Email
                Address
              </label>
              <div style={modalStyles.inputWrap}>
                <span style={modalStyles.inputIcon}>✉️</span>
                <input
                  type="email"
                  placeholder="Enter recipient's email address"
                  value={emailTo}
                  onChange={(e) => setEmailTo(e.target.value)}
                  style={modalStyles.input}
                />
              </div>
            </div>
            <div style={modalStyles.footer}>
              <button
                onClick={closeEmailModal}
                className="btn btn-light"
                style={modalStyles.cancel}
              >
                Cancel
              </button>
              <button
                onClick={sendEmailNow}
                className="btn btn-primary"
                disabled={sendingEmail}
                style={modalStyles.send}
              >
                <span style={{ marginRight: 6 }}>✉️</span>
                {sendingEmail ? "Sending..." : "Send Invoice"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const modalStyles = {
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.35)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1050,
  },
  modal: {
    width: 560,
    background: "#fff",
    borderRadius: 14,
    boxShadow: "0 20px 60px rgba(0,0,0,.25)",
    overflow: "hidden",
  },
  header: {
    padding: "16px 18px",
    borderBottom: "1px solid #edf2f7",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  closeBtn: {
    border: "none",
    background: "transparent",
    fontSize: 24,
    lineHeight: 1,
    cursor: "pointer",
    color: "#718096",
  },
  body: { padding: 18 },
  summary: {
    background: "#eef6ff",
    border: "1px solid #cfe3ff",
    borderRadius: 8,
    padding: 14,
    marginBottom: 16,
  },
  label: {
    display: "block",
    fontWeight: 600,
    color: "#4a5568",
    marginBottom: 8,
  },
  inputWrap: { position: "relative" },
  inputIcon: { position: "absolute", left: 12, top: 9, opacity: 0.7 },
  input: {
    width: "100%",
    height: 40,
    padding: "0 12px 0 36px",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    outline: "none",
  },
  footer: {
    padding: "12px 18px 18px",
    display: "flex",
    justifyContent: "flex-end",
    gap: 12,
  },
  cancel: { borderRadius: 8, padding: "8px 14px" },
  send: {
    borderRadius: 8,
    padding: "8px 14px",
    background: "#25B740",
    borderColor: "#25B740",
  },
};

export default VendorInvoicesPage;